import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiValidation } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import {
  ALLOWED_VALUE_SOURCE_FIELDS,
  buildClientKey,
  canEditAttributeValues,
  canEnableAsFilter,
  isAllowedValueSourceField,
  isDuplicateAllowedValue,
  showsAllowedValuesEditor,
  showsSourceField,
  STORE_ATTRIBUTE_VALUE_TYPES,
  StoreAttributeCategoryViewModel,
  StoreAttributeDetail,
  StoreAttributeValueType,
  VALUE_TYPE_LABELS,
} from '../../../../models/store-attribute.model';
import { StoreAttributeService } from '../../../../services/store-attribute.service';

export interface AddEditAttributeTypeDialogData {
  preselectedCategoryId?: string;
  categories: StoreAttributeCategoryViewModel[];
}

type ApiErrorContext = 'save';

function valueSourceFieldValidator(control: AbstractControl): ValidationErrors | null {
  if (isAllowedValueSourceField(control.value)) {
    return null;
  }

  return { allowedSourceField: true };
}

@Component({
  selector: 'app-add-edit-attribute-type-dialog',
  templateUrl: './add-edit-attribute-type-dialog.component.html',
  styleUrls: ['./add-edit-attribute-type-dialog.component.scss'],
})
export class AddEditAttributeTypeDialogComponent implements OnInit {
  formGroup!: FormGroup;
  allowedValues: string[] = [];
  newValueInput = '';
  readonly valueTypes = STORE_ATTRIBUTE_VALUE_TYPES;
  readonly valueTypeLabels = VALUE_TYPE_LABELS;
  readonly sourceFieldOptions = ALLOWED_VALUE_SOURCE_FIELDS;
  isSaving = false;

  private previousValueType: StoreAttributeValueType | '' = '';
  private readonly errorSnackBarConfig = {
    verticalPosition: 'top' as const,
    panelClass: ['snackbar-error'],
  };

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<AddEditAttributeTypeDialogComponent>,
    private storeAttributeService: StoreAttributeService,
    private matSnackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: AddEditAttributeTypeDialogData,
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      AttributeTypeName: ['', Validators.required],
      CategoryId: [this.data.preselectedCategoryId ?? '', Validators.required],
      ValueType: ['', Validators.required],
      ValueSourceField: [''],
      FilterEnabled: [false],
    });

    this.onValueTypeChanged('' as StoreAttributeValueType);

    this.formGroup.get('ValueType')?.valueChanges.subscribe((valueType) => {
      this.onValueTypeChanged(valueType as StoreAttributeValueType);
    });

    this.formGroup.get('ValueSourceField')?.valueChanges.subscribe((sourceField) => {
      this.onSourceFieldChanged(sourceField as string);
    });
  }

  get title(): string {
    return 'Add Attribute Type';
  }

  get isFilterCheckboxDisabled(): boolean {
    const valueType = this.formGroup.get('ValueType')?.value as StoreAttributeValueType;
    return !canEnableAsFilter(valueType);
  }

  get showSourceField(): boolean {
    const valueType = this.formGroup.get('ValueType')?.value as StoreAttributeValueType;
    return showsSourceField(valueType);
  }

  get showAllowedValuesEditor(): boolean {
    const valueType = this.formGroup.get('ValueType')?.value as StoreAttributeValueType;
    const sourceField = this.formGroup.get('ValueSourceField')?.value as string;
    return canEditAttributeValues(valueType, sourceField);
  }

  get saveButtonLabel(): string {
    return 'Create Attribute Type';
  }

  addValue(): void {
    const value = this.newValueInput.trim();
    if (!value) {
      return;
    }
    if (isDuplicateAllowedValue(this.allowedValues, value)) {
      this.matSnackBar.open('This value already exists', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }
    this.allowedValues = [...this.allowedValues, value];
    this.newValueInput = '';
  }

  removeValue(value: string): void {
    this.allowedValues = this.allowedValues.filter((item) => item !== value);
  }

  onValueInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addValue();
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = this.formGroup.getRawValue();
    const valueType = formValue.ValueType as StoreAttributeValueType;
    const sourceField = showsSourceField(valueType)
      ? formValue.ValueSourceField?.trim() || undefined
      : undefined;
    const values = this.resolveValuesToSave(valueType, sourceField, this.allowedValues);

    const payload = {
      Detail: {
        AttributeTypeName: formValue.AttributeTypeName.trim(),
        ValueType: valueType,
        ValueSourceField: sourceField,
        FilterEnabled: canEnableAsFilter(valueType) ? !!formValue.FilterEnabled : false,
        Values: values,
        ClientKey: buildClientKey(formValue.AttributeTypeName),
      } as StoreAttributeDetail,
      CategoryId: formValue.CategoryId,
    };

    this.isSaving = true;
    this.storeAttributeService.createDomain(payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.storeAttributeService.reload();
        this.matSnackBar.open('Attribute type created', 'OK', DEFAULT_SNACKBAR_CONFIG);
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving = false;
        this.handleApiError(error, 'save');
      },
    });
  }

  private onSourceFieldChanged(sourceField: string): void {
    if (sourceField?.trim()) {
      this.allowedValues = [];
      this.newValueInput = '';
    }
  }

  private onValueTypeChanged(valueType: StoreAttributeValueType): void {
    const filterControl = this.formGroup.get('FilterEnabled');

    if (!valueType) {
      this.formGroup.patchValue({ FilterEnabled: false }, { emitEvent: false });
      filterControl?.disable({ emitEvent: false });
      this.previousValueType = valueType;
      return;
    }

    if (!canEnableAsFilter(valueType)) {
      this.formGroup.patchValue({ FilterEnabled: false }, { emitEvent: false });
      filterControl?.disable({ emitEvent: false });
    } else {
      filterControl?.enable({ emitEvent: false });
      if (this.previousValueType === 'Text' || !this.previousValueType) {
        this.formGroup.patchValue({ FilterEnabled: true }, { emitEvent: false });
      }
    }

    const sourceFieldControl = this.formGroup.get('ValueSourceField');
    if (!showsSourceField(valueType)) {
      this.formGroup.patchValue({ ValueSourceField: '' });
      sourceFieldControl?.clearValidators();
    } else {
      sourceFieldControl?.setValidators([valueSourceFieldValidator]);
    }
    sourceFieldControl?.updateValueAndValidity({ emitEvent: false });

    if (!showsAllowedValuesEditor(valueType)) {
      this.allowedValues = [];
      this.newValueInput = '';
    }

    this.previousValueType = valueType;
  }

  private resolveValuesToSave(valueType: StoreAttributeValueType, sourceField: string | undefined, allowedValues: string[]): string[] {
    if (sourceField?.trim()) {
      return [];
    }

    if (showsAllowedValuesEditor(valueType)) {
      return [...allowedValues];
    }

    return [];
  }

  private handleApiError(error: HttpErrorResponse, context: ApiErrorContext): void {
    if (error.status === 500) {
      this.matSnackBar.open('Error saving attribute type', 'Error', this.errorSnackBarConfig);
      return;
    }

    if (error.status === 400) {
      this.handleBadRequest(error);
      return;
    }

    if (error.status === 429) {
      this.matSnackBar.open(
        `Too many requests. Please wait a moment and retry. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    if (error.status === 504 || error.status === 408) {
      this.matSnackBar.open(
        `Server is taking too long to respond. Please try again. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    if (error.status === 0) {
      this.matSnackBar.open(
        `Request timed out or network issue. Check your connection. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    const message =
      error?.error?.message ||
      error?.message ||
      `Unexpected error. (statusCode: ${error.status})`;
    this.matSnackBar.open(message, 'Error', this.errorSnackBarConfig);
  }

  private handleBadRequest(error: HttpErrorResponse): void {
    const apiValidations = error.error;

    if (Array.isArray(apiValidations) && typeof apiValidations[0] === 'string') {
      this.matSnackBar.open(apiValidations[0], 'Error', this.errorSnackBarConfig);
      return;
    }

    if (Array.isArray(apiValidations)) {
      const errorMessages: string[] = [];
      apiValidations.forEach((validation: ApiValidation) => {
        if (!validation?.PropertyName) {
          if (validation?.ErrorMessage) {
            errorMessages.push(validation.ErrorMessage);
          }
          return;
        }

        const control = this.resolveFormControl(validation.PropertyName);
        if (control) {
          control.markAsTouched();
          control.setErrors({
            ...(control.errors || {}),
            apiError: validation.ErrorMessage,
          });
        } else if (validation.ErrorMessage) {
          errorMessages.push(validation.ErrorMessage);
        }
      });

      if (errorMessages.length > 0) {
        this.matSnackBar.open(errorMessages[0], 'Error', this.errorSnackBarConfig);
      }
      this.formGroup.updateValueAndValidity();
      return;
    }

    if (apiValidations?.error?.message) {
      this.matSnackBar.open(apiValidations.error.message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (apiValidations?.message) {
      this.matSnackBar.open(apiValidations.message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (typeof apiValidations === 'string') {
      this.matSnackBar.open(apiValidations, 'Error', this.errorSnackBarConfig);
      return;
    }

    const fallbackMessage =
      typeof apiValidations === 'object' && apiValidations?.Message
        ? apiValidations.Message
        : 'Validation error';
    this.matSnackBar.open(fallbackMessage, 'Error', this.errorSnackBarConfig);
  }

  private resolveFormControl(propertyName: string) {
    const propertyMap: Record<string, string> = {
      AttributeTypeName: 'AttributeTypeName',
      CategoryId: 'CategoryId',
      ValueType: 'ValueType',
      ValueSourceField: 'ValueSourceField',
      FilterEnabled: 'FilterEnabled',
      'Detail.AttributeTypeName': 'AttributeTypeName',
      'Detail.ValueSourceField': 'ValueSourceField',
      'Detail.FilterEnabled': 'FilterEnabled',
      'Detail.ValueType': 'ValueType',
    };

    const controlName = propertyMap[propertyName] ?? propertyName;
    return this.formGroup.get(controlName);
  }
}
